import { useMutation, type MutateResult } from "@vue/apollo-composable"
import { computed, reactive } from "vue"

import { type Column } from "@/types"
import {
  type Table,
  type MutationRootCreateTablePresetArgs,
  type MutationRootRemoveTablePresetsArgs,
  type MutationRootSetDefaultPresetArgs,
  type MutationRootUnsetDefaultPresetArgs,
  type MutationRootUpdateTablePresetArgs,
  type QueryRootTablePresetsArgs,
  type TablePreset,
  type TablePresetList,
} from "@/graphql/types"
import CreateTablePresetMutation from "@/graphql/preset/CreateTablePreset.gql"
import RemoveTablePresetsMutation from "@/graphql/preset/RemoveTablePresets.gql"
import SetDefaultTablePresetMutation from "@/graphql/preset/SetDefaultTablePreset.gql"
import TablePresetListQuery from "@/graphql/preset/TablePresetList.gql"
import UnsetDefaultTablePresetMutation from "@/graphql/preset/UnsetDefaultTablePreset.gql"
import UpdateTablePresetMutation from "@/graphql/preset/UpdateTablePreset.gql"
import { useSessionStore } from "@/store/session"
import { useApi } from "@/utils/composables/apollo/useApi"
import { useTablePresetConfigurationShared } from "@/utils/composables/useTablePresetConfigurationShared"
import { useOnNextResult } from "@/utils/misc"

export type TablePresetListResult = { tablePresets: TablePresetList }
export type TablePresetCreateResult = { createTablePreset: TablePreset }
export type TablePresetUpdateResult = { updateTablePreset: TablePreset }
export type TablePresetRemoveResult = { removeTablePresets: number }
export type SetDefaultTablePresetResult = { setDefaultPreset: boolean }
export type UnsetDefaultTablePresetResult = { unsetDefaultPreset: boolean }

export const tablePresetResultMap = {
  getList: (result: TablePresetListResult) => result.tablePresets,
  getCreated: (result: TablePresetCreateResult) => result.createTablePreset,
  getUpdated: (result: TablePresetUpdateResult) => result.updateTablePreset,
  getRemovedCount: (result: TablePresetRemoveResult) => result.removeTablePresets,
  getLinkedCount: undefined,
}

export function useColumnPreset<TData, TMappedData = TData>(
  table: Table | undefined,
  presetColumns: Column<TData, TMappedData>[],
  listRouteName: string | string[]
) {
  const sessionStore = useSessionStore()

  const sharedTablePresetConfiguration = useTablePresetConfigurationShared(
    table,
    presetColumns,
    listRouteName
  )

  const {
    listQueryVariables,
    listQueryOptions,

    initialized,

    presets,
    isPresetLoaded,
    selectedPresetId,
    selectedColumns,
  } = sharedTablePresetConfiguration

  const selectedColumnFields = computed(
    () => new Set(selectedColumns.value.flatMap((c) => c.fields ?? [c.key]))
  )

  const api = useApi<
    TablePreset,
    "tablePresets",
    TablePresetListResult,
    QueryRootTablePresetsArgs,
    undefined,
    object,
    TablePresetCreateResult,
    MutationRootCreateTablePresetArgs,
    TablePresetUpdateResult,
    MutationRootUpdateTablePresetArgs,
    TablePresetRemoveResult,
    MutationRootRemoveTablePresetsArgs
  >({
    typename: "TablePreset",
    operations: {
      list: TablePresetListQuery,
      getById: undefined,
      create: CreateTablePresetMutation,
      update: UpdateTablePresetMutation,
      remove: RemoveTablePresetsMutation,
      link: undefined,
    },
    resultMap: tablePresetResultMap,
    mapRemovedIds: (variables) => variables.ids,
    listQueryVariables,
    listQueryOptions,
  })

  api.addRemoveReducer(api.getListFilterRemoveReducer((ids) => (item) => !ids.includes(item.id)))

  api.onResult((result) => (presets.value = result.data?.tablePresets?.items ?? []))

  void api.whenListResultAvailable.then(async () => {
    if (presetColumns.length) {
      await useOnNextResult(initialized)
      isPresetLoaded.value = true
    }
  })

  const defaultLoading = computed(() => setDefaultLoading.value || unsetDefaultLoading.value)

  const { mutate: setDefaultPreset, loading: setDefaultLoading } = useMutation<
    SetDefaultTablePresetResult,
    MutationRootSetDefaultPresetArgs
  >(SetDefaultTablePresetMutation, {
    errorPolicy: "all",
    update: api.prepareListCacheReducer((cachedQuery, _data, variables) => {
      const list = tablePresetResultMap.getList(cachedQuery)
      for (const item of list.items) item.isUserDefault = item.id === variables.id
      if (sessionStore.isUserAdmin) {
        for (const item of list.items) item.isGlobalDefault = item.id === variables.id
      }
      return cachedQuery
    }),
  })

  const { mutate: unsetDefaultPreset, loading: unsetDefaultLoading } = useMutation<
    UnsetDefaultTablePresetResult,
    MutationRootUnsetDefaultPresetArgs
  >(UnsetDefaultTablePresetMutation, {
    errorPolicy: "all",
    update: api.prepareListCacheReducer((cachedQuery) => {
      const list = tablePresetResultMap.getList(cachedQuery)
      for (const item of list.items) item.isUserDefault = false
      return cachedQuery
    }),
  })

  async function create(name: string, columns: string[]): MutateResult<TablePresetCreateResult> {
    if (!table) throw Error("Table is not provided!")
    const result = await api.create({ name, table, columns })
    selectedPresetId.value = result?.data?.createTablePreset.id
    return result
  }

  async function setDefault(id: string): MutateResult<SetDefaultTablePresetResult> {
    if (!table) throw Error("Table is not provided!")
    return await setDefaultPreset({ table, id })
  }

  async function unsetDefault(): MutateResult<UnsetDefaultTablePresetResult> {
    if (!table) throw Error("Table is not provided!")
    return await unsetDefaultPreset({ table })
  }

  return reactive({
    column: {
      table,
      api,
      defaultLoading,

      selectedColumnFields,
      ...sharedTablePresetConfiguration,

      create,
      setDefault,
      unsetDefault,
    },
  })
}
